import { Scene } from "three";
import { validate as validateUuid } from 'uuid';
import Sequence from "./Sequence";
import Utils from "../Utilities";
import Engine from "../Engine";
import store, { StoreActions } from "../../state/store";
import { CreateTimeline, GetTimeline } from "../timeline";
import { ChecklistFormType, ChecklistType, SimulationStates, TrackerTypes } from "../../globals";
import ARTracker from "../ARTracker";
import { assert } from "../utilities/ErrorHandling";
import DefaultSceneManager from "../DefaultSceneManager";
import QuizManager from "../QuizManager";
import SceneObjectUtils from "../utilities/SceneObjectUtils";
import templateTypes from "../../components/uisystem/templates/types";
import { IStepDataObject, IStepParams, ISceneParams, StepSaveData, StepExportData, StepCloneOptions, IStepUIInfo } from "../../interfaces/ScenarioElements";
import { StepUIData, UISubcomponentData, UITemplateData} from "../../interfaces/UITemplateData";
import { IChecklist } from "../../interfaces/checklistItem";
import { Timeline } from "../timeline/Timeline";
import { traverseGrapesProject } from "../../components/visageUI/GrapesJSUtilities";
import { GjsAttributeNames, GjsMetaTypes } from "../../components/visageUI/gjsplugin/Enums";
import { getUuidv4, JsonCopy } from "../utilities/Misc";
import {APIField, APIMethod, RegisterClassWithAPI} from "../api/Decorators";
import { ScenarioElementTypes } from "../../interfaces/enums";
import Camera from "../Camera";
import { emptyDomRect, IDomRect } from "../../interfaces/Misc";
import { scenarioMap } from ".";


const stepDataObject : IStepDataObject = {
	id: "",
	name: "",
	type: ScenarioElementTypes.Step,
	index: 0,
	simulationSkip: false,
	parentId: "",
	isDefaultStep: false,
	isTrackerStep: false,
	isUserDefinedStep: false,
};

/**
 * defines a single, independent step of an instruction set, with its own hierarchy, ui information, timeline animation, etc.
 * @export
 * @class Step
 */
@RegisterClassWithAPI("Step", { sequence: "Sequence"}, "A single step in a sequence. Creating a step class will not add the step to a sequence. Use the AddStepAction to add a step to a sequence.")
export default class Step
{
	#id: string;
	sequence: Sequence;
	label: string;
	copyOf?: string;
	private _sceneId: string | null;
	private _scene: Scene | null;
	private _timeline: Timeline | undefined;
	checklistData: IChecklist | null;
	uiData: StepUIData | null;
	trackers: string[] | null;
	uiInfo: IStepUIInfo;
	connectedToDefault: boolean;
	textToVoice: string;

	@APIField({ description: "The step's timeline object. Returns undefined on default step and tracker step types.", type: "Timeline | undefined" })
	get timeline(): Timeline | undefined
	{
		return this._timeline;
	}

	get scene(): Scene
	{
		return this._scene!;
	}
	get sceneId(): string
	{
		return this._sceneId!;
	}

	/**
	* Creates an instance of Step.
	* @param {Sequence} sequence this is the sequence the step will associate with (the parent of this step)
	* @param {{
	* [name="Step"]: String,
	* [label=""]: String,
	* [connectedToDefault=true]: Boolean
	* [isDefaultStep=false]: Boolean,
	* [autogenerate=true]: Boolean,
	* [sceneParams={}]: {[isDefaultScene=isDefaultStep]: Boolean, defaultSceneId: String, sourceSceneGuid: String}
	* }} [params={}] contains optional parameters to modify step creation:
	* - name: a unique name for this step. *subject to Utils.getUniqueElementName. defaults to "Step"*
	* - connectedToDefault: if true, this step will inherit the default state and will synchronize objects with it. *defaults to 'true'*
	* - isDefaultState: if true, it will mark this Step as the Defautl State step of its parent sequence. *there should only be 1 Default State step in each sequence*
	* - autogenerate: if true, the Step will automatically generate for itself all the data it needs to function correctly, e.g. creating a Timeline instance, creating UI data instance, etc.
	* - sceneParams: taken into account when *autogenerate = true*, this data object will be used to modify the step's scene creation
	* - sceneParams.isDefaultScene: if true, it lets the created scene know that it should inherit and synchronizte with the Default State scene
	* - sceneParams.defaultSceneId: the guid of the sequence's Default State scene. *if isDefaultState is set to 'true', this value should be left undefined*
	* - sceneParams.sourceSceneGuid: an optional guid of a scene this step should copy. *if isDefaultState is set to 'true', this value should be left undefined. optional*
	* @memberof Step
	*/
	constructor(sequence: Sequence, params: IStepParams = {})
	{
		assert(sequence);

		const options: IStepParams = {
			connectedToDefault: true,
			isDefaultStep: false,
			isTrackersStep: false,
			name: "Step 1",
			label: "",
			autogenerate: true,
			sceneParams: {},
			...params
		};

		this.#id = getUuidv4();
		this.sequence = sequence;
		this.name = options.name || '';
		this.label = options.label || '';
		this.copyOf = options.copyOf;
		this._sceneId = null;
		this._scene = null;
		this.uiData = null;
		this.checklistData = null;
		this.trackers = null;
		this.uiInfo = {
			sequenceEditorRect: emptyDomRect(),
			sequenceBarRect: emptyDomRect(),
		};
		this.textToVoice = "";

		const dataObject: IStepDataObject = {
			...stepDataObject,
			id: this.id,
			name: Utils.getUniqueElementName(this.sequence.steps, options.name ?? "Step 1"),
			parentId: this.sequence.id,
			isDefaultStep: options.isDefaultStep ?? false,
			isTrackerStep: options.isTrackersStep ?? false,
			isUserDefinedStep: !options.isDefaultStep && !options.isTrackersStep,
		};
		store.dispatch(StoreActions.scenario.setElementData({ dataId: this.id, data: dataObject }));

		if (options.autogenerate)
		{
			const sceneParams: ISceneParams = {
				isDefaultScene: options.isDefaultStep,
				defaultSceneId: options.connectedToDefault ? this.sequence.defaultStep.sceneId : undefined,
				sourceSceneGuid: options.connectedToDefault ? this.sequence.defaultStep.sceneId : undefined,
				isTrackersStep: options.isTrackersStep,
				...options.sceneParams
			};

			this._sceneId = Engine.getInstance().createScene(sceneParams);
			this._scene = Engine.getInstance().getScene(this._sceneId);

			if (!options.isDefaultStep && !options.isTrackersStep)
			{
				this._timeline = CreateTimeline(this.id, this._sceneId);
				this.uiData = null;
				this.checklistData = null;
				// const slideTemplate = { ...store.getState().uiModule.slideMaster[templateTypes.BLANK], slide: templateTypes.BLANK, synced: true };
				// delete slideTemplate.name;
				// delete slideTemplate.type;

				// default to "None" template
				store.dispatch(StoreActions.ui.setFromCopy({ stepId: this.id, content: { slide: "", synced: false } }));
			}

			if (options.isTrackersStep)
			{
				if (options.dontMakeTracker)
				{
					this.trackers = [];
				}
				else
				{
					//add a new default surface tracker and connect it to this step
					const tracker = new ARTracker(TrackerTypes.SLAM);
					Engine.getInstance().addObject(tracker, ARTracker.trackersRoot, this._sceneId);
					this.trackers = [tracker.userData.guid];
				}
			}
		}

		if (options.isTrackersStep) this.name = "Content Placement";
		this.connectedToDefault = options.isTrackersStep ? true : (options.connectedToDefault ?? true);

		scenarioMap.set(this.id, this);
	}

	getType() : string
	{
		return ScenarioElementTypes.Step;
	}

	getDataObject() : IStepDataObject
	{
		return store.getState().scenarioModule.elementData[this.id];
	}

	@APIField({ type: "string" })
	get id() : string
	{
		return this.#id;
	}

	set id(newId : string)
	{
		if (!newId)
		{
			console.error("Step ID cannot be empty or null");
			return;
		}

		if (!validateUuid(newId))
		{
			console.error(`Invalid GUID format for Step ID: ${newId}`);
			return;
		}

		const currentDataObject = { ...this.getDataObject() };
		store.dispatch(StoreActions.scenario.removeElementData(this.id));

		this.#id = newId;
		store.dispatch(StoreActions.scenario.setElementData({ dataId: this.id, data: { ...currentDataObject, id: newId } }));
	}

	get name() : string
	{
		return this.getDataObject().name;
	}

	@APIField({ type: "string" })
	set name(newName : string)
	{
		store.dispatch(StoreActions.scenario.setElementProperty({ elementId: this.id, propertyName: "name", propertyValue: newName }));
	}

	get index() : number
	{
		return this.sequence.steps.indexOf(this);
	}

	set index(newIndex: number)
	{
		this.sequence.steps.splice(this.index, 1);
		this.sequence.steps.splice(newIndex, 0, this);
	}

	get uiTemplateId()
	{
		return store.getState().visageModule.stepTemplateMap[this.id];
	}

	get simulationSkip() : boolean
	{
		return this.getDataObject().simulationSkip;
	}

	set simulationSkip(value: boolean)
	{
		store.dispatch(StoreActions.scenario.setElementProperty({ elementId: this.id, propertyName: "simulationSkip", propertyValue: value }));
	}

	get parentId() : string
	{
		return this.getDataObject().parentId;
	}

	set parentId(newParentId: string)
	{
		store.dispatch(StoreActions.scenario.setElementProperty({ elementId: this.id, propertyName: "parentId", propertyValue: newParentId }));
	}

	get isUserDefinedStep() : boolean
	{
		return this.getDataObject().isUserDefinedStep;
	}

	/**
 * @returns all the data necessary to save and load this step from a scenario save file
 * @memberof Step
 */
	save() : StepSaveData
	{
		return {
			id: this.id,
			inObject: [],
			outObject: [],
			name: this.name,
			parent: this.sequence.id,
			sceneId: this.sceneId,
			connectedToDefault: this.connectedToDefault,
			trackers: this.trackers,
			textToVoice: this.textToVoice,
		};
	}

	/**
 * @returns all the data necessary for playback in the viewer app
 * @memberof Step
 */
	export(objectGuidToNodeIndexMap: Map<string, number> | undefined = undefined) : StepExportData
	{
		// Exports focus ID of the interactive camera, falls back to default if it does not exist. 
		// Should this be stored on the steps?
		const interactiveCamera = Camera.getInteractiveModeCamera(this.scene);
		const focusTargetGUID = interactiveCamera?.userData.interactiveCameraFocusPoint;

		const exportedStep : StepExportData = {
			name: this.name,
			parent: this.sequence.id,
			sceneId: this.sceneId,
			textToVoice: this.textToVoice,
			focusTarget: focusTargetGUID ? objectGuidToNodeIndexMap?.get(focusTargetGUID) ?? null : null,
		};
		if (this.trackers)
		{
			exportedStep.trackers = this.trackers;
		}

		return exportedStep;
	}

	load(data : StepSaveData)
	{
		assert(data);

		this.clear();
		scenarioMap.delete(this.id);

		this.id = data.id;
		this.name = data.name;
		this.connectedToDefault = data.trackers ? false : data.connectedToDefault;
		this._sceneId = data.sceneId;
		this._scene = Engine.getInstance().getScene(this._sceneId);
		this._timeline = GetTimeline(this.id);
		this.trackers = data.trackers;
		this.textToVoice = data.textToVoice;

		scenarioMap.set(this.id, this);

		if(this.trackers)
		{
			store.dispatch(StoreActions.scenario.setElementProperty({ elementId: this.id, propertyName: "isTrackerStep", propertyValue: true }));
			store.dispatch(StoreActions.scenario.setElementProperty({ elementId: this.id, propertyName: "isUserDefinedStep", propertyValue: false }));
		}
	}

	clear()
	{
	}

	/**
 * @returns true if this step is the same one set to be the default state in the parent sequence
 * @memberof Step
 */
	isDefault() : boolean
	{
		return this.sequence.defaultStep === this;
	}

	isTrackersStep() : boolean
	{
		return !this.trackers ? false : true;
	}

	isBeforeTrackersStep() : boolean
	{
		if (this.sequence.trackersStep.sequence.id !== this.sequence.id) return false;
		return this.getIndex() < this.sequence.trackersStep.getIndex();
	}

	isDisabledTrackersStep() : boolean
	{
		return this.isTrackersStep() && this.id !== this.getParent().trackersStep.id;
	}

	getIndex() : number
	{
		return this.sequence.steps.indexOf(this);
	}

	isBeforeStep(step : Step) : boolean
	{
		assert(step, "impossible to know step order with undefined or null parameter");

		if(this.sequence.getIndex() > step.sequence.getIndex()) return false;
		if(this.sequence.getIndex() < step.sequence.getIndex()) return true;
		return this.getIndex() < step.getIndex();
	}

	isAfterStep(step : Step) : boolean
	{
		assert(step, "impossible to know step order with undefined or null parameter");

		if(this.sequence.getIndex() > step.sequence.getIndex()) return true;
		if(this.sequence.getIndex() < step.sequence.getIndex()) return false;
		return this.getIndex() > step.getIndex();
	}

	move(newSequence : Sequence, start = false) : void
	{
		const previousSequence = this.sequence;
		if (this.sequence.id !== newSequence.id)
		{
			if (start)
			{
				newSequence.steps.unshift(this);
			}
			else
			{
				newSequence.steps.push(this);
			}
			this.sequence = newSequence;
			previousSequence.removeStep(this);
		}

		DefaultSceneManager.getInstance().onSceneMoved(this._scene!, this.sequence.defaultStep.scene, true);
	}

	dispose() : void
	{
		this.sequence.removeStep(this);
		scenarioMap.delete(this.id);
	}

	getParent() : Sequence
	{
		return this.sequence;
	}

	clone(options: StepCloneOptions = {}): Step
	{
		const params: StepCloneOptions = {
			...{
				sequence: this.sequence,
				continues: false
			},
			...options
		};

		if(!params.sequence) params.sequence = this.sequence;
		if(!params.continues) params.continues = false;

		// make the new step
		const newStep = new Step(params.sequence, {
			name: this.name,
			label: "Copy of " + this.sequence.name + "." + this.name,
			copyOf: this.id,
			connectedToDefault: this.connectedToDefault,
			isDefaultStep: this.isDefault(),
			autogenerate: false,
		});

		// reset timeline before copying the scene
		let timelineCurrentTime = 0;
		if (this._timeline)
		{
			timelineCurrentTime = this._timeline.currentTime;
			if (params.continues)
			{
				this._timeline.update(this._timeline.getEndTime());
			}
			else
			{
				this._timeline.resetAnimations();
			}
		}

		// copy the scene
		const sceneParams: ISceneParams = {
			isDefaultScene: this.isDefault(),
			isContinueScene: params.continues,
			sourceSceneGuid: this._sceneId as string,
			sourceStepGuid: this.id,
			...(this.connectedToDefault && { defaultSceneId: params.sequence.defaultStep._sceneId as string}),
		};

		newStep._sceneId = Engine.getInstance().createScene(sceneParams);
		newStep._scene = Engine.getInstance().getScene(newStep._sceneId);

		if (this._timeline)
		{
			// restore timeline
			if (this.id === store.getState().scenarioModule.currentStepId)
			{
				this._timeline.update(timelineCurrentTime);
			}
			else
			{
				this._timeline.resetAnimations();
			}

			// copy the timeline
			if (options.continues)
			{
				newStep._timeline = CreateTimeline(newStep.id, newStep._sceneId);
			}
			else
			{
				newStep._timeline = this.timeline?.clone(newStep.id, newStep._sceneId ? newStep._sceneId : newStep._scene?.userData.guid);
			}
		}

		const myChecklistData = store.getState().checklistModule.checklist[this.id] ? JSON.parse(JSON.stringify(store.getState().checklistModule.checklist[this.id])) : undefined; //this object is read-only otherwise
		if (myChecklistData)
		{
			if(myChecklistData.type === ChecklistFormType.QUIZ && (myChecklistData.questions[0].type === ChecklistType.MULTI_OPTION_3D || myChecklistData.questions[0].type === ChecklistType.MULTI_OPTION_SELECT_3D))
			{
				for(const option of myChecklistData.questions[0].options)
				{
					if(option.object)
					{
						option.object = SceneObjectUtils.getSceneCopyGuidMap().get(option.object);
						const sceneObject = SceneObjectUtils.findObjectByGuid(Engine.getInstance().getSelectionRoot(newStep._scene?.userData.guid), option.object);
						if(sceneObject) QuizManager.quizObjectMap.set(option.object, sceneObject);
					}
				}
			}
			store.dispatch(StoreActions.checklist.setFromCopy({ stepId: newStep.id, content: { ...myChecklistData } }));
		}

		const myStepConditions = store.getState().scenarioModule.stepConditions[this.id] ? JSON.parse(JSON.stringify(store.getState().scenarioModule.stepConditions[this.id])) : undefined; //this object is read-only otherwise
		if (myStepConditions)
		{
			for(const condition of myStepConditions)
			{
				for(const trigger of condition.triggers)
				{
					if(trigger.targetObject)
					{
						trigger.targetObject = SceneObjectUtils.getSceneCopyGuidMap().get(trigger.targetObject);
					}
				}

			}
			store.dispatch(StoreActions.scenario.pasteStepConditions({ stepId: newStep.id, clipboard: [ ...myStepConditions ] }));
		}

		const myUIData = store.getState().uiModule.ui[this.id] ? JSON.parse(JSON.stringify(store.getState().uiModule.ui[this.id])) as StepUIData : undefined;
		if (myUIData)
		{
			// must not copy quiz results
			if (myUIData.slide !== templateTypes.QUIZ_RESULTS)
			{
				//change old camera reference to new camera reference
				for (const rawTemplate of Object.values(myUIData))
				{
					const template = rawTemplate as UISubcomponentData;
					if (template["Insets"])
					{
						for (const insetKey of Object.keys(template["Insets"]))
						{
							if (insetKey.endsWith("camera"))
							{
								const insetProperty = template["Insets"][insetKey];
								if (insetProperty.value)
								{
									const copiedGuid = SceneObjectUtils.getSceneCopyGuidMap().get(insetProperty.value as string);
									insetProperty.value = copiedGuid;
								}
							}
						}
					}
				}
				store.dispatch(StoreActions.ui.setFromCopy({ stepId: newStep.id, content: myUIData }));
			}
			else
			{
				// set default to "Blank" template
				// const blankTemplate = { ...store.getState().uiModule.slideMaster[templateTypes.BLANK], slide: templateTypes.BLANK, synced: true };
				// store.dispatch(StoreActions.ui.setFromCopy({ stepId: newStep.id, content: { ...blankTemplate }}));

				// default to "None" template
				store.dispatch(StoreActions.ui.setFromCopy({ stepId: newStep.id, content: { slide: "", synced: false } }));
			}
		}

		const myAttachments = store.getState().attachmentsModule.attachments[this.id];
		if(myAttachments)
		{
			store.dispatch(StoreActions.attachments.setStepAttachments({ stepId: newStep.id, assetIds: [...myAttachments] }));
		}

		// clone new UI data
		const oldUiData = store.getState().visageModule.stepUIData[this.id];
		if(oldUiData && !this.isDefault() && !this.isTrackersStep())
		{
			const myNewUIData = JsonCopy(oldUiData);

			// for camera blocks, we need to find the old camera id and copy over new pairings with the new camera id
			const oldToNewCameraMapping: { [oldCameraId: string]: string } = {};
			traverseGrapesProject(oldUiData, ({component}) =>
			{
				if(component.attributes && component.attributes[GjsAttributeNames.MetaType] === GjsMetaTypes.Camera)
				{
					const cameraId = component.attributes[GjsAttributeNames.ParamCameraID];
					if(cameraId)
					{
						oldToNewCameraMapping[cameraId] = SceneObjectUtils.getSceneCopyGuidMap().get(cameraId)!;
					}
				}
			});

			// update camera ids to new ones
			traverseGrapesProject(myNewUIData, ({component}) =>
			{
				if(component.attributes && component.attributes[GjsAttributeNames.MetaType] === GjsMetaTypes.Camera)
				{
					const cameraId = component.attributes[GjsAttributeNames.ParamCameraID];
					if(cameraId)
					{
						component.attributes[GjsAttributeNames.ParamCameraID] = oldToNewCameraMapping[cameraId];

						store.dispatch(StoreActions.visage.setCameraCanvasPairing({ cameraId: oldToNewCameraMapping[cameraId], canvasId: component.attributes[GjsAttributeNames.ID] }));
					}
				}
			});

			// set same template
			store.dispatch(StoreActions.visage.setStepTemplate({ stepId: newStep.id, templateId: this.uiTemplateId }));

			// set copied and fixed ui data - must happen after setting the template as it overwrites the step ui data!
			store.dispatch(StoreActions.visage.setStepUIData({ stepId: newStep.id, data: { ...myNewUIData } }));
		}

		return newStep;
	}

	reverse() : void
	{
		if (this.isTrackersStep()) return;
		if (!this._timeline) return;
		this._timeline.reverse();
	}

	getNextStep() : Step | undefined
	{
		if (store.getState().applicationModule.simulationState === SimulationStates.Playing)
		{
			let nextStep = this.sequence.getNextStep(this);
			if (!nextStep) return;

			while (nextStep?.simulationSkip || (	nextStep.isTrackersStep() && nextStep.id !== nextStep.sequence.trackersStep.id))
			{
				nextStep = nextStep.sequence.getNextStep(nextStep);

				// Again, check if nextStep is null after updating it
				if (!nextStep) return;
			}
			return nextStep;
		}
		
		return this.sequence.getNextStep(this);
	}

	getPreviousStep() : Step | undefined
	{
		if (store.getState().applicationModule.simulationState === SimulationStates.Playing)
		{
			let prevStep = this.sequence.getPreviousStep(this);
			if (!prevStep) return;
			
			while (prevStep?.simulationSkip || (prevStep.isTrackersStep() && prevStep.id !== prevStep.sequence.trackersStep.id))
			{
				prevStep = prevStep.sequence.getPreviousStep(prevStep);

				// Again, check if nextStep is null after updating it
				if (!prevStep) return;
			}
			return prevStep;
		}
		
		return this.sequence.getPreviousStep(this);
	}

	isDeleted() : boolean
	{
		return this.sequence.isDeleted() || this.sequence.steps.indexOf(this) < 0;
	}
}
